import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Sparkles, Eye, BookOpen, MessageCircle, Shuffle, LogIn, User, Moon, Star, Lock, ArrowRight, History } from 'lucide-react';
import { useAuth } from '@/components/AuthContext';
import PlaylistToggle from '@/components/PlaylistToggle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MysteriaeHomePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [featuredCards, setFeaturedCards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedCards();
  }, []);

  const fetchFeaturedCards = async () => {
    try {
      const response = await fetch(`${API_URL}/api/mysteriae/cards`);
      const data = await response.json();
      // Get 6 random cards for the featured section
      const shuffled = [...data.cards].sort(() => 0.5 - Math.random());
      setFeaturedCards(shuffled.slice(0, 6));
    } catch (error) {
      console.error('Error fetching cards:', error);
    } finally {
      setLoading(false);
    }
  };

  const MODULES = [
    {
      id: 'draw',
      name: 'Faire un Tirage',
      description: 'Tirez les cartes et recevez une interprétation personnalisée par IA',
      icon: Sparkles,
      path: '/mysteriae/draw',
      isFree: false,
      features: ['5 types de tirages', 'Interprétation IA', '78 cartes disponibles']
    },
    {
      id: 'arcanes',
      name: 'Les 78 Arcanes',
      description: 'Explorez les 22 Majeurs et 56 Mineurs avec leurs symboliques',
      icon: Eye,
      path: '/mysteriae/arcanes',
      isFree: true,
      features: ['78 cartes détaillées', 'Filtres par élément', '4 couleurs mineures']
    },
    {
      id: 'random',
      name: 'Carte du Jour',
      description: 'Tirez une carte pour votre méditation quotidienne',
      icon: Star,
      path: '/mysteriae/random',
      isFree: false,
      isPremium: true,
      features: ['Tirage rapide', 'Message du jour', 'Majeurs et Mineurs']
    },
    {
      id: 'quiz',
      name: 'Quiz Tarot',
      description: 'Testez vos connaissances sur les Arcanes Majeurs',
      icon: BookOpen,
      path: '/mysteriae/quiz',
      isFree: true,
      features: ['20 questions', 'Explications détaillées', 'Score final']
    },
    {
      id: 'knowledge',
      name: 'Découvrir',
      description: 'Apprenez tout sur le Tarot et son symbolisme',
      icon: BookOpen,
      path: '/mysteriae/knowledge',
      isFree: false,
      isPremium: true,
      features: ['Histoire du Tarot', 'Symbolisme', 'Conseils pratiques']
    },
    {
      id: 'chat',
      name: 'Poser une Question',
      description: 'Discutez avec Arcana, notre experte en Tarot',
      icon: MessageCircle,
      path: '/mysteriae/chat',
      isFree: true,
      features: ['Chat IA', 'Réponses personnalisées', 'Apprentissage guidé']
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 via-white to-purple-50">
      {/* Subtle decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-64 h-64 bg-purple-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-indigo-200/20 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-purple-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link 
              to="/" 
              className="flex items-center gap-2 text-purple-600 hover:text-purple-700 transition-colors"
              data-testid="back-to-reveliae"
            >
              <ChevronLeft className="h-5 w-5" />
              <span className="font-semibold">REVELIAE</span>
            </Link>
            <span className="text-gray-300">/</span>
            <div className="flex items-center gap-2">
              <Moon className="h-5 w-5 text-purple-600" />
              <span className="font-bold text-gray-900 tracking-wide">MYSTERIAE</span>
            </div>
          </div>
          
          <div>
            {isAuthenticated ? (
              <div className="flex items-center gap-3">
                <span className="text-gray-600 text-sm hidden sm:block">
                  {user?.first_name || user?.name}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={logout}
                  className="border-purple-200 text-purple-700 hover:bg-purple-50 rounded-xl"
                >
                  <User className="h-4 w-4 mr-2" />
                  Déconnexion
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/login')}
                className="border-purple-200 text-purple-700 hover:bg-purple-50 rounded-xl"
                data-testid="login-btn"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Connexion
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full border border-purple-200 mb-6">
              <Sparkles className="h-4 w-4 text-purple-600" />
              <span className="text-purple-700 text-sm font-medium">Les Arcanes Majeurs</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
              <span className="text-gray-900">Explorez les mystères</span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600">
                du Tarot
              </span>
            </h1>
            
            <p className="text-gray-600 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
              Un voyage introspectif à travers les 22 Arcanes Majeurs.
              Le Tarot n'est pas un outil de prédiction, mais un miroir de l'âme
              pour mieux se comprendre.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Modules Section */}
      <section className="px-4 pb-12">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {MODULES.map((module, index) => {
              const Icon = module.icon;
              const isAvailable = module.isFree || isAuthenticated;
              
              return (
                <motion.div
                  key={module.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className={`group ${isAvailable ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                  onClick={() => isAvailable && navigate(module.path)}
                  data-testid={`module-${module.id}`}
                >
                  <div className={`
                    relative p-6 rounded-2xl border transition-all duration-300 h-full
                    ${isAvailable 
                      ? 'bg-white border-purple-200 hover:border-purple-400 hover:shadow-lg hover:shadow-purple-100' 
                      : 'bg-gray-50 border-gray-200 opacity-75'}
                  `}>
                    {/* Badge */}
                    <div className="absolute top-4 right-4">
                      {module.isFree ? (
                        <span className="px-2 py-1 text-xs font-semibold bg-purple-100 text-purple-700 rounded-full">
                          Gratuit
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
                          <Lock className="h-3 w-3" />
                          Premium
                        </span>
                      )}
                    </div>
                    
                    {/* Icon */}
                    <div className={`
                      w-14 h-14 rounded-xl flex items-center justify-center mb-4
                      ${isAvailable 
                        ? 'bg-gradient-to-br from-purple-500 to-indigo-600' 
                        : 'bg-gray-300'}
                    `}>
                      <Icon className="h-7 w-7 text-white" />
                    </div>
                    
                    {/* Content */}
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {module.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      {module.description}
                    </p>
                    
                    {/* Features */}
                    <ul className="space-y-2">
                      {module.features.map((feature, i) => (
                        <li key={i} className="text-sm text-gray-500 flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    {/* CTA */}
                    {isAvailable && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <span className="text-purple-600 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                          Explorer
                          <ArrowRight className="h-4 w-4" />
                        </span>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Cards Grid */}
      <section className="py-12 px-4 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Aperçu des Arcanes</h2>
            <p className="text-gray-600">22 cartes, 22 clés pour comprendre le voyage de l'âme</p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {loading ? (
              [...Array(6)].map((_, i) => (
                <div key={i} className="aspect-[2/3] bg-purple-100 rounded-xl animate-pulse" />
              ))
            ) : (
              featuredCards.map((card, index) => (
                <motion.div
                  key={card.number}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                >
                  <Link
                    to={`/mysteriae/arcanes/${card.number}`}
                    className="block group"
                    data-testid={`featured-card-${card.number}`}
                  >
                    <div className="relative aspect-[2/3] rounded-xl overflow-hidden border-2 border-purple-200 group-hover:border-purple-400 transition-all duration-300 group-hover:shadow-xl group-hover:shadow-purple-200/50">
                      <img
                        src={card.image_url}
                        alt={card.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-2 left-2 right-2">
                          <p className="text-white text-sm font-semibold truncate">{card.name}</p>
                          <p className="text-purple-300 text-xs">{card.number}</p>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))
            )}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="text-center mt-8"
          >
            <Button
              onClick={() => navigate('/mysteriae/arcanes')}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl"
              data-testid="view-all-arcanes-btn"
            >
              Voir les 22 Arcanes
              <Star className="h-4 w-4 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="p-6 bg-purple-50 rounded-2xl border border-purple-100">
            <Moon className="h-8 w-8 text-purple-500 mx-auto mb-3" />
            <p className="text-gray-600 text-sm italic leading-relaxed">
              "Le Tarot ne prédit pas l'avenir - il éclaire le présent.
              Les cartes sont des miroirs qui reflètent nos questionnements intérieurs
              et nous invitent à la réflexion."
            </p>
          </div>
        </div>
      </section>

      {/* Playlist Toggle */}
      <PlaylistToggle theme="purple" app="mysteriae" />
    </div>
  );
};

export default MysteriaeHomePage;
